/**
 * Paddle.js v2 со скрипта Paddle (https://developer.paddle.com/paddle-js/overview): без npm-пакета.
 * `Environment.set` до `Initialize`; `Initialize` один раз на странице.
 */
interface PaddleGlobal {
  Environment: { set(env: "sandbox" | "production"): void };
  Initialize(opts: { token: string; eventCallback?: (e: { name?: string }) => void }): void;
  Checkout: {
    open(opts: {
      items: Array<{ priceId: string; quantity: number }>;
      customData?: Record<string, string>;
      customer?: { email: string };
      settings?: { displayMode?: "overlay"; successUrl?: string; locale?: string };
    }): void;
  };
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

const SCRIPT_URL = "https://cdn.paddle.com/paddle/v2/paddle.js";
let loading: Promise<PaddleGlobal> | null = null;
let initializedToken: string | null = null;
let onEvent: ((name: string) => void) | null = null;

function loadScript(): Promise<PaddleGlobal> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => (window.Paddle ? resolve(window.Paddle) : reject(new Error("Paddle.js не загрузился")));
    s.onerror = () => {
      loading = null;
      reject(new Error("Paddle.js не загрузился"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export interface CheckoutParams {
  environment: "sandbox" | "production";
  clientToken: string;
  items: Array<{ priceId: string; quantity: number }>;
  customData: { salonId: string };
  customerEmail: string | null;
  successUrl: string;
  locale: string;
  /** События оверлея (`checkout.completed`, `checkout.closed`). */
  onEvent?: (name: string) => void;
}

export async function openPaddleCheckout(p: CheckoutParams): Promise<void> {
  const paddle = await loadScript();
  onEvent = p.onEvent ?? null;
  if (initializedToken !== p.clientToken) {
    if (p.environment === "sandbox") paddle.Environment.set("sandbox");
    paddle.Initialize({ token: p.clientToken, eventCallback: (e) => e.name && onEvent?.(e.name) });
    initializedToken = p.clientToken;
  }
  paddle.Checkout.open({
    items: p.items,
    customData: p.customData,
    ...(p.customerEmail ? { customer: { email: p.customerEmail } } : {}),
    settings: { displayMode: "overlay", successUrl: p.successUrl, locale: p.locale },
  });
}
